import { DisclosureCollectionProps, DisclosureGroupingsForCollectionProps, DisclosureWithTitleProps } from './types';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import DisclosureCollectionTableOfContents from './DisclosureCollectionTableOfContents';
import { cn } from 'utils/styles';
import { getLetterLabel } from 'utils/formatters';

/**
 * Generates a URL-safe anchor ID from a title
 */
const generateAnchorId = (title: string, index: number): string => {
  const slug =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'grouping';
  return `grouping-${index + 1}-${slug}`;
};

/**
 * Generates a URL-safe anchor ID for an individual disclosure within a grouping
 */
const generateDisclosureAnchorId = (
  groupingIndex: number,
  disclosureIndex: number,
  disclosureTitle: string,
): string => {
  const slug =
    disclosureTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'disclosure';
  return `grouping-${groupingIndex + 1}-${disclosureIndex + 1}-${slug}`;
};

/**
 * Checks if a grouping has any valid disclosure content
 */
const hasValidContent = (grouping: DisclosureGroupingsForCollectionProps): boolean => {
  const disclosures = grouping.disclosures?.items ?? [];
  return disclosures.some(disclosure => disclosure?.copy?.json || disclosure?.disclosureItem?.copy?.json);
};

/**
 * DisclosureCollection renders multiple DisclosureGroupings with a shared
 * table of contents. Each grouping has a title that becomes a TOC entry
 * and heading, containing disclosure items with inline bold titles.
 * Disclosures with titles are listed as lettered sub-items (A, B, C) in
 * the TOC and prefixed with matching letters in the body.
 */
const DisclosureCollection = ({ groupingsCollection, tableOfContentsExpanded }: DisclosureCollectionProps) => {
  const groupings = (groupingsCollection?.items ?? []).filter(Boolean);

  // Filter groupings to only include those with valid title and content
  const groupingsWithContent = groupings.filter(
    (grouping): grouping is DisclosureGroupingsForCollectionProps => !!grouping.title && hasValidContent(grouping),
  );

  if (groupingsWithContent.length === 0) {
    return null;
  }

  // Generate TOC items from grouping titles, with child entries for titled disclosures
  const tocItems = groupingsWithContent.map((grouping, index) => {
    const disclosures = (grouping.disclosures?.items ?? []).filter(Boolean);
    const disclosuresWithContent = disclosures.filter(d => d.copy?.json || d.disclosureItem?.copy?.json);
    const titledDisclosures = disclosuresWithContent.filter(d => !!d.title);

    const children = titledDisclosures.map((d, titleIndex) => ({
      label: d.title ?? '',
      anchor: generateDisclosureAnchorId(index, titleIndex, d.title ?? ''),
    }));

    return {
      label: grouping.title ?? '',
      anchor: generateAnchorId(grouping.title ?? `grouping-${index}`, index),
      children,
    };
  });

  return (
    <section className="content-wrapper py-4">
      <div className="max-w-[765px]">
        {/* Table of Contents - Client Component for interactivity */}
        {tocItems.length > 0 && (
          <DisclosureCollectionTableOfContents items={tocItems} defaultExpanded={tableOfContentsExpanded ?? true} />
        )}

        {/* Disclosure Groupings */}
        <div className="space-y-12">
          {groupingsWithContent.map((grouping, index) => (
            <DisclosureGroupingItem
              key={grouping.sys?.id ?? index}
              anchorId={generateAnchorId(grouping.title ?? `grouping-${index}`, index)}
              groupingIndex={index}
              {...grouping}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface DisclosureGroupingItemProps extends DisclosureGroupingsForCollectionProps {
  anchorId: string;
  groupingIndex: number;
}

/**
 * Renders a single DisclosureGrouping within the collection.
 * The grouping title becomes a numbered heading, and each disclosure's title
 * is rendered inline (bolded) before the copy. Titled disclosures are
 * prefixed with letters (A, B, C) when there are multiple titled disclosures.
 */
const DisclosureGroupingItem = ({
  anchorId,
  groupingIndex,
  title,
  disclosures: disclosuresData,
}: DisclosureGroupingItemProps) => {
  const disclosures = (disclosuresData?.items ?? []).filter(Boolean);

  // Filter disclosures to only include those with valid copy content
  const disclosuresWithContent = disclosures.filter(
    disclosure => disclosure.copy?.json || disclosure.disclosureItem?.copy?.json,
  );

  if (disclosuresWithContent.length === 0) {
    return null;
  }

  const titledDisclosures = disclosuresWithContent.filter(d => !!d.title);
  const hasTitledDisclosures = titledDisclosures.length > 0;
  let letterIndex = 0;

  return (
    <section id={anchorId} className="scroll-mt-24">
      {/* Grouping title as numbered heading */}
      <h2 className="flex font-sans text-heading-sm md:text-heading-md font-semibold mb-6">
        <span className="shrink-0 w-8 md:w-10">{groupingIndex + 1}.</span>
        <span>{title}</span>
      </h2>

      {/* Disclosure items with inline bold titles */}
      <div className={cn('space-y-6', hasTitledDisclosures && 'pl-8 md:pl-10')}>
        {disclosuresWithContent.map((disclosure, disclosureIndex) => {
          const hasTitleAndLetter = hasTitledDisclosures && !!disclosure.title;
          const currentLetterIndex = hasTitleAndLetter ? letterIndex++ : -1;
          const letter = hasTitleAndLetter ? getLetterLabel(currentLetterIndex) : undefined;
          const disclosureAnchorId = hasTitleAndLetter
            ? generateDisclosureAnchorId(groupingIndex, currentLetterIndex, disclosure.title ?? '')
            : undefined;

          return (
            <DisclosureItem
              key={disclosure.sys?.id ?? disclosureIndex}
              letter={letter}
              indented={hasTitledDisclosures}
              disclosureAnchorId={disclosureAnchorId}
              {...disclosure}
            />
          );
        })}
      </div>
    </section>
  );
};

interface DisclosureItemProps extends DisclosureWithTitleProps {
  letter?: string;
  indented?: boolean;
  disclosureAnchorId?: string;
}

/**
 * Renders a single disclosure with its title inline (bolded) before the copy.
 * When indented, the content is offset to align with lettered siblings.
 * When a letter is provided, the disclosure is also prefixed with the letter.
 */
const DisclosureItem = ({ title, copy, disclosureItem, letter, indented, disclosureAnchorId }: DisclosureItemProps) => {
  // Use copy field if available, otherwise fall back to legacy disclosureItem
  const copyContent = copy || disclosureItem?.copy;

  if (!copyContent?.json) {
    return null;
  }

  return (
    <div id={disclosureAnchorId} className={indented ? 'flex scroll-mt-24' : undefined}>
      {indented && <span className="shrink-0 w-8 md:w-10 font-sans font-bold">{letter ? `${letter}.` : ''}</span>}
      <div className="font-serif text-body-serif">
        {title && <span className="font-sans font-bold">{title} </span>}
        <RichTextCopy copy={copyContent} />
      </div>
    </div>
  );
};

export default DisclosureCollection;
